import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { getSyncQueue } from '../lib/api';
import {
  LayoutDashboard,
  Users as UsersIcon,
  Package,
  FileText,
  LogOut,
  Menu,
  X,
  UserCog,
  Layers,
  Sun,
  Moon
} from 'lucide-react';
import SyncStatus from './SyncStatus';
import BrandLogo from './BrandLogo';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [syncCount, setSyncCount] = useState(0);

  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDark]);

  useEffect(() => {
    const updateSyncCount = async () => {
      const queue = await getSyncQueue();
      setSyncCount(queue.length);
    };

    updateSyncCount();
    const interval = setInterval(updateSyncCount, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const showLegacyNav = String(process.env.REACT_APP_ENABLE_LEGACY_UI || '').toLowerCase() === 'true';

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['ADMIN', 'OWNER', 'MUNIM', 'OPERATOR'] },
    { name: 'User Management', href: '/users', icon: UserCog, roles: ['OWNER'] },
    { name: 'Party Master', href: '/parties', icon: UsersIcon, roles: ['ADMIN', 'OWNER', 'MUNIM'] },
    { name: 'Coil In Entry', href: '/coil-in', icon: Package, roles: ['ADMIN', 'OWNER', 'MUNIM'] },
    { name: 'Programs', href: '/programs', icon: Layers, roles: ['ADMIN', 'OWNER', 'MUNIM', 'OPERATOR'] },
    { name: 'Scrap Dispatch', href: '/scrap-dispatch', icon: Package, roles: ['ADMIN', 'OWNER', 'MUNIM'] },
    { name: 'Accounts', href: '/accounts', icon: FileText, roles: ['ADMIN', 'OWNER', 'MUNIM'] },
    { name: 'Audit Log', href: '/audit-log', icon: FileText, roles: ['ADMIN', 'OWNER'] },
    ...(showLegacyNav
      ? [
          { name: 'Production Jobs (Legacy)', href: '/jobs', icon: Package, roles: ['ADMIN', 'OWNER', 'OPERATOR'] },
          { name: 'Scrap (Legacy)', href: '/scrap', icon: Package, roles: ['ADMIN', 'OWNER', 'MUNIM', 'OPERATOR'] },
          { name: 'Dispatch (Legacy)', href: '/dispatch', icon: Package, roles: ['ADMIN', 'OWNER', 'MUNIM'] },
          { name: 'Dies (Legacy)', href: '/dies', icon: Package, roles: ['ADMIN', 'OWNER', 'MUNIM', 'OPERATOR'] },
          { name: 'Program Changes (Legacy)', href: '/program-changes', icon: Layers, roles: ['ADMIN', 'OWNER', 'MUNIM'] },
        ]
      : []),
  ];

  const filteredNavigation = navigation.filter((item) => item.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-industrial-bg text-industrial-text overflow-x-hidden font-sans" data-testid="layout">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-[#000000]/60 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div
        className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-industrial-s1 border-r border-industrial-border transform transition-transform duration-200 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
      `}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-industrial-border">
            <BrandLogo size={32} className="mb-2" />
            <p className="text-xs text-industrial-text-muted mt-1">{user?.full_name}</p>
            <p className="text-xs font-mono text-accent font-bold uppercase">{user?.role}</p>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-[2px] font-mono text-xs uppercase tracking-wider transition-colors duration-75
                    ${
                      isActive
                        ? 'bg-accent text-white font-bold border-l-4 border-white'
                        : 'text-industrial-text-muted hover:bg-industrial-s2 hover:text-industrial-text'
                    }
                  `}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="h-4.5 w-4.5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-industrial-border">
            <SyncStatus count={syncCount} />
            <button 
              data-testid="logout-button" 
              className="w-full mt-4 h-11 bg-industrial-s2 border border-industrial-border hover:bg-industrial-s1 hover:border-accent text-industrial-text rounded-[2px] font-mono text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-colors duration-75" 
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 text-[#FF1744]" />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="lg:pl-64 w-full overflow-x-hidden flex flex-col min-h-screen">
        <header className="sticky top-0 z-30 bg-industrial-s1 border-b border-industrial-border h-16 flex items-center justify-between px-4">
          <button className="lg:hidden text-industrial-text-muted hover:text-industrial-text" onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="mobile-menu-toggle">
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-4">
            <button
              onClick={() => setIsDark(!isDark)}
              className="p-2 border border-industrial-border text-industrial-text-muted hover:text-industrial-text hover:bg-industrial-s2 rounded-[2px] transition-colors font-mono text-[10px] uppercase tracking-wider flex items-center gap-1.5"
              title="Toggle Theme"
              data-testid="theme-toggle"
            >
              {isDark ? (
                <>
                  <Sun className="h-3.5 w-3.5 text-accent animate-pulse" />
                  <span className="hidden sm:inline">Light Mode</span>
                </>
              ) : (
                <>
                  <Moon className="h-3.5 w-3.5 text-accent" />
                  <span className="hidden sm:inline">Dark Mode</span>
                </>
              )}
            </button>
            <div className="text-right hidden sm:block">
              <p className="font-mono text-xs uppercase tracking-wider text-industrial-text font-semibold">{user?.full_name}</p>
              <p className="font-mono text-[10px] text-industrial-text-muted">{user?.email}</p>
            </div>
          </div>
        </header>

        <main className="p-4 md:p-6 max-w-[1600px] mx-auto w-full flex-1 bg-industrial-bg">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
